import { create } from 'zustand';

const FCM_TOKEN_KEY = 'fcmDeviceToken';

/**
 * Zustand store for client-side notification state.
 *
 * Normalised notification shape:
 *   { id, title, message, isRead, createdAt, data }
 *
 * `data` may contain: { action_type, params (parsed object), id }
 */
export const useNotificationStore = create((set, get) => ({
  notifications: [],
  fcmToken: localStorage.getItem(FCM_TOKEN_KEY) || null,

  /** Replace the full list (called when React Query syncs from backend). */
  setNotifications: (list) => set({ notifications: list }),

  /**
   * Optimistically prepend a new notification.
   * No-op if a notification with the same id already exists (deduplication).
   */
  addNotification: (notification) => {
    const existing = get().notifications;
    if (existing.some((n) => n.id === notification.id)) return;
    set({ notifications: [notification, ...existing] });
  },

  /** Mark a single notification as read (client-side). */
  markAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n
      ),
    })),

  /** Mark every notification as read (client-side). */
  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
    })),

  /** Persist FCM token locally and in state. */
  setFcmToken: (token) => {
    localStorage.setItem(FCM_TOKEN_KEY, token);
    set({ fcmToken: token });
  },

  /** Remove FCM token (called on logout). */
  clearFcmToken: () => {
    localStorage.removeItem(FCM_TOKEN_KEY);
    set({ fcmToken: null });
  },
}));
